import * as _ from 'jsonous'
import { combineEpics, ofType, StateObservable } from 'redux-observable'
import { catchError, filter, map, mapTo, mergeMap, Observable, of, switchMap, withLatestFrom } from 'rxjs'
import { ajax, AjaxError, AjaxResponse } from 'rxjs/ajax'

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { decodeAndGet } from '../utils'

import type { RootState } from '.'
import { orderDecoder } from './decoders'
import type { CornerStatus, Order, OrderStatus } from './types'

type Status = 'idle' | 'fetching' | 'error' | 'success'

interface UpdateStatus {
  outletOrderIdList: number[]
  status: CornerStatus
}

interface State {
  status: Status
  errorMessage: string | null
  data: Order | null
  isLoading: boolean
}

const initialState: State = {
  status: 'idle',
  errorMessage: null,
  data: null,
  isLoading: false,
}

export const slice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    reset: (state) => {
      state.status = 'idle'
      state.errorMessage = null
      state.data = null
    },

    loadStart: (state, _: PayloadAction<number>) => {
      state.status = 'fetching'
      state.errorMessage = null
    },
    loadFail: (state, { payload }: PayloadAction<string>) => {
      state.status = 'error'
      state.errorMessage = payload
    },
    loadSuccess: (state, { payload }: PayloadAction<Order>) => {
      state.status = 'success'
      state.errorMessage = null
      state.data = payload
    },

    updateCornerStatusStart: (state, { payload }: PayloadAction<UpdateStatus>) => {
      if (state.data) {
        state.data.order_outlets.forEach((outletOrder) => {
          if (payload.outletOrderIdList.includes(outletOrder.id)) {
            outletOrder.isLoading = true
          }
        })
      }
    },
    updateCornerStatusFail: (state, { payload }: PayloadAction<UpdateStatus>) => {
      if (state.data) {
        state.data.order_outlets.forEach((outletOrder) => {
          if (payload.outletOrderIdList.includes(outletOrder.id)) {
            outletOrder.isLoading = false
          }
        })
      }
    },
    updateCornerStatusSuccess: (state, { payload }: PayloadAction<UpdateStatus>) => {
      if (state.data) {
        state.data.order_outlets.forEach((outletOrder) => {
          if (payload.outletOrderIdList.includes(outletOrder.id)) {
            outletOrder.status = payload.status
            outletOrder.isLoading = false
          }
        })
      }
    },

    updateOrderStatusStart: (state) => {
      state.isLoading = true
    },
    updateOrderStatusFail: (state) => {
      state.isLoading = false
    },
    updateOrderStatusSuccess: (state) => {
      state.isLoading = false

      if (state.data) {
        state.data.ready_status = 'received'
      }
    },

    updateOrderStatus: (state, { payload }: PayloadAction<OrderStatus>) => {
      if (state.data) {
        state.data.ready_status = payload
      }
    },
  },
})

// --- EPICS --- //

const BASE_URL = process.env.BASE_URL

const loadEpic = (action$: Observable<any>, state$: StateObservable<RootState>) =>
  action$.pipe(
    ofType('order/loadStart'),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]: [{ payload: number }, RootState]) =>
      ajax.get(`${BASE_URL}/arm/runner/order/${payload}`, { authorization: `Bearer ${state.auth.accessToken}` }).pipe(
        map(({ response }: AjaxResponse<unknown>) => {
          const [data, errorMessage] = decodeAndGet(orderDecoder, response)

          if (data) {
            return slice.actions.loadSuccess(data)
          } else {
            return slice.actions.loadFail(errorMessage)
          }
        }),
        catchError((error: AjaxError) => {
          return of(slice.actions.loadFail(error.message))
        }),
      ),
    ),
  )

const updateCornerStatusEpic = (action$: Observable<any>, state$: StateObservable<RootState>) =>
  action$.pipe(
    ofType('order/updateCornerStatusStart'),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]: [{ payload: UpdateStatus }, RootState]) => {
      return of(...payload.outletOrderIdList).pipe(
        mergeMap(
          (outletOrderId) =>
            ajax
              .get(`${BASE_URL}/arm/order/${outletOrderId}/status/${payload.status}`, {
                authorization: `Bearer ${state.auth.accessToken}`,
              })
              .pipe(
                mapTo(slice.actions.updateCornerStatusSuccess(payload)),
                catchError(() => of(slice.actions.updateCornerStatusFail(payload))),
              ),
          1,
        ),
      )
    }),
  )

const updateOrderStatusEpic = (action$: Observable<any>, state$: StateObservable<RootState>) =>
  action$.pipe(
    ofType('order/updateOrderStatusStart'),
    withLatestFrom(state$),
    switchMap(([, state]) =>
      ajax
        .post(
          `${PROXY_API}/ninja/order/${state.order.data?.id}/full/status/received`,
          {},
          { authorization: `Bearer ${state.auth.accessToken}` },
        )
        .pipe(
          mapTo(slice.actions.updateOrderStatusSuccess()),
          catchError(() => of(slice.actions.updateOrderStatusFail())),
        ),
    ),
  )

const catchOrderChange = (action$: Observable<any>, state$: StateObservable<RootState>) =>
  action$.pipe(
    filter(({ type }) => type === 'SOCKET/CHANGE_ORDER'),
    withLatestFrom(state$),
    map(([{ data }, state]: [{ data: { order_id: string; status_id: string } }, RootState]) => {
      const orderId = Number(data.order_id)

      if (isNaN(orderId)) return { type: 'NoOp' }

      const currentOrder = state.order.data

      if (!currentOrder) return { type: 'NoOp' }

      const isMatch = currentOrder.order_outlets.map(({ id }) => id).includes(orderId)

      return isMatch ? slice.actions.loadStart(currentOrder.id) : { type: 'NoOp' }
    }),
  )

export const epics = combineEpics<any, any, any, unknown>(
  loadEpic,
  updateCornerStatusEpic,
  updateOrderStatusEpic,
  catchOrderChange,
)
